import React, { useState, useEffect } from 'react';
import { XCircle, CheckCircle, Users, Clock, AlertCircle, Check, X, FileText, Calendar, User, Building, Package } from 'lucide-react';
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

// ✅ HIGH AUTHORITY POSITIONS - These auto-approve ALL levels below them
const HIGH_AUTHORITY_POSITIONS = ['VP-Finance/Admin', 'President/CEO'];

const PRApprovalModal = ({
    isOpen,
    onClose,
    selectedPR,
    onApprove,
    onReject,
    getStatusBadge,
    formatDate
}) => {
    const [approverPositions, setApproverPositions] = useState([]);
    const [prDetails, setPrDetails] = useState([]);
    const [prApprovals, setPrApprovals] = useState([]);
    const [approverSignatures, setApproverSignatures] = useState({});
    const [approverDetails, setApproverDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [expenseTypeName, setExpenseTypeName] = useState("");
    const [locationName, setLocationName] = useState("");
    const [departmentName, setDepartmentName] = useState("");
    const [prTypeName, setPrTypeName] = useState("");
    const [paymentMethodName, setPaymentMethodName] = useState("");

    const [canApprovePR, setCanApprovePR] = useState(false);
    const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
    const [denialReason, setDenialReason] = useState("");

    const [requestors, setRequestors] = useState([]);
    const [secondApprovals, setSecondApprovals] = useState([]);
    const [users, setUsers] = useState([]);

    // ─── Fetch helpers ────────────────────────────────────────────────────────

    const fetchRequestors = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Requestor`);
            const data = await res.json();
            setRequestors(data);
        } catch (error) { console.error(error); }
    };

    const fetchSecondApprovals = async (prHeaderId) => {
        try {
            const res = await fetch(`${API_BASE_URL}api/SecondApproverPR`);
            if (!res.ok) throw new Error("Failed to fetch second approvals");
            const data = await res.json();
            const prSecondApprovals = data.filter(a => a.prHeaderId === prHeaderId);
            setSecondApprovals(prSecondApprovals);
            console.log("📋 SecondApprovals fetched:", prSecondApprovals);
        } catch (err) { console.error("Second approval error:", err); }
    };

    useEffect(() => {
        if (!selectedPR?.companyId) return;
        fetch(`${API_BASE_URL}api/Company/${selectedPR.companyId}`)
            .then(r => r.json()).then(d => setCompanyName(d.companyName || "N/A")).catch(() => setCompanyName("N/A"));
    }, [selectedPR?.companyId]);

    useEffect(() => {
        if (!selectedPR?.expenseTypeId) return;
        fetch(`${API_BASE_URL}api/ExpenseType/${selectedPR.expenseTypeId}`)
            .then(r => r.json()).then(d => setExpenseTypeName(d.expenseTypeName || "N/A")).catch(() => setExpenseTypeName("N/A"));
    }, [selectedPR?.expenseTypeId]);

    useEffect(() => {
        if (!selectedPR?.locationId) return;
        fetch(`${API_BASE_URL}api/Location/${selectedPR.locationId}`)
            .then(r => r.json()).then(d => setLocationName(d.locationName || "N/A")).catch(() => setLocationName("N/A"));
    }, [selectedPR?.locationId]);

    useEffect(() => {
        if (!selectedPR?.chargedToDept) return;
        fetch(`${API_BASE_URL}api/Department/${selectedPR.chargedToDept}`)
            .then(r => r.json()).then(d => setDepartmentName(d.departmentName || "N/A")).catch(() => setDepartmentName("N/A"));
    }, [selectedPR?.chargedToDept]);

    useEffect(() => {
        if (!selectedPR?.prType) return;
        fetch(`${API_BASE_URL}api/M_PR_Type/${selectedPR.prType}`)
            .then(r => r.json()).then(d => setPrTypeName(d.prTypeName || "N/A")).catch(() => setPrTypeName("N/A"));
    }, [selectedPR?.prType]);

    useEffect(() => {
        if (!selectedPR?.paymentMethodId) return;
        fetch(`${API_BASE_URL}api/PaymentMethod/${selectedPR.paymentMethodId}`)
            .then(r => r.json()).then(d => setPaymentMethodName(d.paymentMethodName || "N/A")).catch(() => setPaymentMethodName("N/A"));
    }, [selectedPR?.paymentMethodId]);

    useEffect(() => {
        if (isOpen && selectedPR) { fetchAllData(); }
    }, [isOpen, selectedPR]);

    useEffect(() => {
        if (API_BASE_URL && selectedPR?.id) { fetchSecondApprovals(selectedPR.id); }
    }, [API_BASE_URL, selectedPR?.id]);

    useEffect(() => {
        if (prApprovals.length > 0) {
            prApprovals.forEach(approval => {
                if (approval.actualApproverId) {
                    fetchUserSignature(approval.actualApproverId);
                    fetchUserDetails(approval.actualApproverId);
                }
            });
            checkPRApprovalPermission();
        }
    }, [prApprovals]);

    useEffect(() => {
        if (secondApprovals.length > 0) {
            secondApprovals.forEach(approval => {
                if (approval.actualApproverId) {
                    fetchUserSignature(approval.actualApproverId);
                    fetchUserDetails(approval.actualApproverId);
                }
            });
        }
    }, [secondApprovals]);

    const fetchAllData = async () => {
        setLoading(true);
        setIsCheckingPermissions(true);
        try {
            await Promise.all([
                fetchApproverPositions(),
                fetchPRDetails(),
                fetchPRApprovals(),
                checkPRApprovalPermission(),
                fetchRequestors()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
            setIsCheckingPermissions(false);
        }
    };

    const checkPRApprovalPermission = async () => {
        try {
            const userSession = localStorage.getItem("user");
            if (!userSession) { setCanApprovePR(false); setDenialReason("No user session found"); return; }

            const sessionData = JSON.parse(userSession);
            const userLevelId = sessionData.userLevelId || sessionData.userLevel;
            const currentUserId = sessionData.id || sessionData.userId;

            const userAlreadyApproved = prApprovals.some(
                a => a.actualApproverId === currentUserId && a.response === 1 && a.isActive === 1
            );
            if (userAlreadyApproved) { setCanApprovePR(false); setDenialReason("You have already approved this Purchase Request"); return; }

            const userAlreadyRejected = prApprovals.some(
                a => a.actualApproverId === currentUserId && a.response === 0 && a.isActive === 1
            );
            if (userAlreadyRejected) { setCanApprovePR(false); setDenialReason("You have already rejected this Purchase Request"); return; }

            const res = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
            if (!res.ok) throw new Error('Failed to fetch approver positions');
            const positions = await res.json();

            const userPosition = positions.find(pos => pos.approverUserLevelId === userLevelId);
            if (!userPosition || userPosition.pr !== 1) {
                setCanApprovePR(false);
                setDenialReason("Your position does not have permission to approve Purchase Requests");
                return;
            }

            setCanApprovePR(true);
            setDenialReason("");
        } catch (error) {
            console.error('Error checking PR approval permission:', error);
            setCanApprovePR(false);
            setDenialReason("Error checking approval permissions");
        }
    };

    const fetchApproverPositions = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
            const data = await res.json();
            setApproverPositions(data);
        } catch (error) { console.error('Error fetching approver positions:', error); }
    };

    const fetchPRDetails = async () => {
        if (!selectedPR?.id) return;
        setLoadingDetails(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${selectedPR.id}`);
            const data = await res.json();
            setPrDetails(Array.isArray(data) ? data : [data]);
        } catch (error) {
            console.error('Error fetching PR details:', error);
            setPrDetails([]);
        } finally { setLoadingDetails(false); }
    };

    const fetchPRApprovals = async () => {
        if (!selectedPR?.id) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/T_PR_Approval`);
            const data = await res.json();
            setPrApprovals(data.filter(a => a.prHeaderId === selectedPR.id));
        } catch (error) { console.error('Error fetching PR approvals:', error); setPrApprovals([]); }
    };

    const fetchUserSignature = async (userId) => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);
            if (res.ok) {
                const blob = await res.blob();
                const imageUrl = URL.createObjectURL(blob);
                setApproverSignatures(prev => ({ ...prev, [userId]: imageUrl }));
            }
        } catch (error) { console.error(`Error fetching signature for user ${userId}:`, error); }
    };

    const fetchUserDetails = async (userId) => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Users/${userId}`);
            if (res.ok) {
                const data = await res.json();
                setApproverDetails(prev => ({ ...prev, [userId]: data }));
            }
        } catch (error) { console.error(`Error fetching user details for ${userId}:`, error); }
    };

    if (!isOpen || !selectedPR) return null;

    const allLevelsApproved = () => {
        const totalLevels = approverPositions.filter(p => p.pr === 1).length || 3;
        const approvedLevels = prApprovals.filter(a => a.response === 1 && a.isActive === 1);
        return approvedLevels.length >= totalLevels;
    };

    const isButtonDisabled = allLevelsApproved() || isSubmitting || isCheckingPermissions || !canApprovePR;

    // ─── CORE: Auto-approve ENDORSEMENT + ACKNOWLEDGEMENT + all lower dynamic levels ──
    // ✅ ENDORSEMENT and ACKNOWLEDGEMENT are fetched by position name (no fixed approvalLevel)
    // ✅ They are stored in SecondApproverPR with approvalType to distinguish them
    const autoApproveLowerLevels = async (sessionData, currentUserLevel, isApprove = true) => {
        const autoApprovedLabels = [];

        const higherAuthorityLabel = approverPositions.find(p => p.colum === currentUserLevel)?.approverPosition || 'Higher Authority';

        // ─── STEP 1: Auto-approve ENDORSEMENT (SecondApproverPR approvalLevel = 2) ───
        const endorsementAlreadyDone = secondApprovals.some(
            a => a.approvalLevel === 2 && (a.response === 1 || a.response === 0)
        );

        if (!endorsementAlreadyDone) {
            console.log("🔄 Auto-processing ENDORSEMENT...");
            try {
                const endorseRes = await fetch(`${API_BASE_URL}api/Users/by-position/Endorsement`);
                if (!endorseRes.ok) throw new Error("Failed to fetch Endorsement user");
                const endorseUsers = await endorseRes.json();
                if (!endorseUsers || endorseUsers.length === 0) throw new Error("No Endorsement user found");
                const endorseUser = endorseUsers[0];

                const endorseData = {
                    id: 0,
                    prHeaderId: selectedPR.id,
                    actualApproverId: endorseUser.id,
                    actualApproverName: `${endorseUser.firstName} ${endorseUser.lastName}`,
                    response: isApprove ? 1 : 0,
                    responseDate: new Date().toISOString(),
                    responseRemarks: isApprove
                        ? `Auto-approved by ${sessionData.firstName} ${sessionData.lastName} (${higherAuthorityLabel})`
                        : `Auto-rejected due to rejection by ${sessionData.firstName} ${sessionData.lastName}`,
                    isActive: 1,
                    approvalLevel: 2   // ✅ ENDORSEMENT = 2
                };

                const endorsePostRes = await fetch(`${API_BASE_URL}api/SecondApproverPR`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(endorseData)
                });

                if (!endorsePostRes.ok) {
                    const errText = await endorsePostRes.text();
                    throw new Error(`Failed to auto-${isApprove ? 'approve' : 'reject'} ENDORSEMENT: ${errText}`);
                }

                await fetchUserSignature(endorseUser.id);
                await fetchUserDetails(endorseUser.id);

                autoApprovedLabels.push(`ENDORSEMENT (${endorseUser.firstName} ${endorseUser.lastName})`);
                console.log(`✅ ENDORSEMENT auto-${isApprove ? 'approved' : 'rejected'}: ${endorseUser.firstName} ${endorseUser.lastName}`);

                await fetch(`${API_BASE_URL}api/Logs`, {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        action: isApprove ? "AUTO-APPROVE" : "AUTO-REJECT",
                        module: "Purchase Request",
                        description: `Auto-${isApprove ? 'approved' : 'rejected'} ENDORSEMENT for PR #${selectedPR.id} by ${sessionData.firstName} ${sessionData.lastName}`,
                        userId: sessionData.id || sessionData.userId,
                        username: sessionData.username,
                        fullName: `${sessionData.firstName} ${sessionData.lastName}`,
                    })
                }).catch(err => console.error("Log failed:", err));

            } catch (err) {
                console.error("❌ ENDORSEMENT auto-approve failed:", err);
                await Swal.fire({
                    icon: "error",
                    title: "Auto-Approval Failed",
                    text: `Could not auto-approve ENDORSEMENT: ${err.message}`
                });
                throw err;
            }
        } else {
            console.log("⏭️ ENDORSEMENT already processed, skipping.");
        }

        // ─── STEP 2: Auto-approve ACKNOWLEDGEMENT (SecondApproverPR approvalLevel = 3) ─
        const acknowledgementAlreadyDone = secondApprovals.some(
            a => a.approvalLevel === 3 && (a.response === 1 || a.response === 0)
        );

        if (!acknowledgementAlreadyDone) {
            console.log("🔄 Auto-processing ACKNOWLEDGEMENT...");
            try {
                const ackRes = await fetch(`${API_BASE_URL}api/Users/by-position/Acknowledgement`);
                if (!ackRes.ok) throw new Error("Failed to fetch Acknowledgement user");
                const ackUsers = await ackRes.json();
                if (!ackUsers || ackUsers.length === 0) throw new Error("No Acknowledgement user found");
                const ackUser = ackUsers[0];

                const ackData = {
                    id: 0,
                    prHeaderId: selectedPR.id,
                    actualApproverId: ackUser.id,
                    actualApproverName: `${ackUser.firstName} ${ackUser.lastName}`,
                    response: isApprove ? 1 : 0,
                    responseDate: new Date().toISOString(),
                    responseRemarks: isApprove
                        ? `Auto-approved by ${sessionData.firstName} ${sessionData.lastName} (${higherAuthorityLabel})`
                        : `Auto-rejected due to rejection by ${sessionData.firstName} ${sessionData.lastName}`,
                    isActive: 1,
                    approvalLevel: 3   // ✅ ACKNOWLEDGEMENT = 3
                };

                const ackPostRes = await fetch(`${API_BASE_URL}api/SecondApproverPR`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(ackData)
                });

                if (!ackPostRes.ok) {
                    const errText = await ackPostRes.text();
                    throw new Error(`Failed to auto-${isApprove ? 'approve' : 'reject'} ACKNOWLEDGEMENT: ${errText}`);
                }

                await fetchUserSignature(ackUser.id);
                await fetchUserDetails(ackUser.id);

                autoApprovedLabels.push(`ACKNOWLEDGEMENT (${ackUser.firstName} ${ackUser.lastName})`);
                console.log(`✅ ACKNOWLEDGEMENT auto-${isApprove ? 'approved' : 'rejected'}: ${ackUser.firstName} ${ackUser.lastName}`);

                await fetch(`${API_BASE_URL}api/Logs`, {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        action: isApprove ? "AUTO-APPROVE" : "AUTO-REJECT",
                        module: "Purchase Request",
                        description: `Auto-${isApprove ? 'approved' : 'rejected'} ACKNOWLEDGEMENT for PR #${selectedPR.id} by ${sessionData.firstName} ${sessionData.lastName}`,
                        userId: sessionData.id || sessionData.userId,
                        username: sessionData.username,
                        fullName: `${sessionData.firstName} ${sessionData.lastName}`,
                    })
                }).catch(err => console.error("Log failed:", err));

            } catch (err) {
                console.error("❌ ACKNOWLEDGEMENT auto-approve failed:", err);
                await Swal.fire({
                    icon: "error",
                    title: "Auto-Approval Failed",
                    text: `Could not auto-approve ACKNOWLEDGEMENT: ${err.message}`
                });
                throw err;
            }
        } else {
            console.log("⏭️ ACKNOWLEDGEMENT already processed, skipping.");
        }

        // ─── STEP 3: Auto-approve dynamic T_PR_Approval levels below currentUserLevel ─
        for (let level = 1; level < currentUserLevel; level++) {
            const alreadyActioned = prApprovals.some(a => a.approvalLevel === level && a.isActive === 1);
            if (alreadyActioned) {
                console.log(`⏭️ T_PR_Approval Level ${level} already actioned, skipping.`);
                continue;
            }

            const levelPosition = approverPositions.find(pos => pos.colum === level && pos.pr === 1);
            if (!levelPosition) {
                console.warn(`⚠️ No position config found for T_PR_Approval Level ${level}, skipping.`);
                continue;
            }

            const positionName = levelPosition.approverPosition;
            let levelUser = null;

  try {
                const posRes = await fetch(`${API_BASE_URL}api/Users/by-position/${encodeURIComponent(positionName)}`);
                if (!posRes.ok) throw new Error(`Failed to fetch user for position ${positionName}`);
                const posUsers = await posRes.json();
                if (!posUsers || posUsers.length === 0) throw new Error(`No user found for position ${positionName}`);
                levelUser = posUsers[0];
            } catch (err) {
                console.warn(`⚠️ Skipping Level ${level} (${positionName}): ${err.message}`);
                continue; // ✅ Skip lang, wag i-throw — mag-proceed sa next level
            }

            const autoData = {
                id: 0,
                prHeaderId: selectedPR.id,
                approverUserLevelId: levelPosition.approverUserLevelId,
                actualApproverId: levelUser.id,
                response: isApprove ? 1 : 0,
                responseDate: new Date().toISOString(),
                responseRemarks: isApprove
                    ? `Auto-approved by ${sessionData.firstName} ${sessionData.lastName} (Level ${currentUserLevel} - ${higherAuthorityLabel})`
                    : `Auto-rejected due to rejection by ${sessionData.firstName} ${sessionData.lastName} (Level ${currentUserLevel})`,
                isActive: 1,
                approvalLevel: level
            };

            const autoRes = await fetch(`${API_BASE_URL}api/T_PR_Approval`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(autoData)
            });
            if (!autoRes.ok) throw new Error(`Failed to auto-${isApprove ? 'approve' : 'reject'} Level ${level}`);

            await fetchUserSignature(levelUser.id);
            await fetchUserDetails(levelUser.id);

            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: isApprove ? "AUTO-APPROVE" : "AUTO-REJECT",
                    module: "Purchase Request",
                    description: `Auto-${isApprove ? 'approved' : 'rejected'} PR #${selectedPR.id} at Level ${level} (${positionName}) by ${sessionData.firstName} ${sessionData.lastName}`,
                    userId: sessionData.id || sessionData.userId,
                    username: sessionData.username,
                    fullName: `${sessionData.firstName} ${sessionData.lastName}`,
                })
            }).catch(err => console.error("Log failed:", err));

            autoApprovedLabels.push(`Level ${level} - ${positionName} (${levelUser.firstName} ${levelUser.lastName})`);
            console.log(`✅ T_PR_Approval Level ${level} auto-${isApprove ? 'approved' : 'rejected'}: ${levelUser.firstName} ${levelUser.lastName}`);
        }

        return autoApprovedLabels;
    };

    // ─── APPROVE ──────────────────────────────────────────────────────────────
    const handleApprove = async () => {
        const userSession = localStorage.getItem("user");
        if (!userSession) {
            await Swal.fire({ icon: "error", title: "No Session", text: "No user session found." });
            return;
        }

        const sessionData = JSON.parse(userSession);
        const currentUserId = sessionData.id || sessionData.userId;
        const userLevelId = sessionData.userLevelId || sessionData.userLevel;

        const { isConfirmed: addRemarks } = await Swal.fire({
            title: "Add remarks?",
            text: "Do you want to add remarks for this approval?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
        });

        let remarksText = "Approved via system";
        if (addRemarks) {
            const { value: customRemarks } = await Swal.fire({
                title: "Enter your remarks",
                input: "textarea",
                inputPlaceholder: "Type your remarks here...",
                showCancelButton: true
            });
            if (customRemarks === undefined) return;
            if (customRemarks) remarksText = customRemarks;
        }

        setIsSubmitting(true);
        try {
            const userPosition = approverPositions.find(pos => pos.approverUserLevelId === userLevelId);
            const currentUserLevel = userPosition?.colum || 1;
            const currentPositionName = userPosition?.approverPosition || '';

            console.log(`🎯 Approving as Level ${currentUserLevel} - ${currentPositionName}`);

            // ✅ Always auto-approve ENDORSEMENT + ACKNOWLEDGEMENT regardless of level
            const autoApprovedLabels = await autoApproveLowerLevels(sessionData, currentUserLevel, true);

            const approvalData = {
                id: 0,
                prHeaderId: selectedPR.id,
                approverUserLevelId: userLevelId,
                actualApproverId: currentUserId,
                response: 1,
                responseDate: new Date().toISOString(),
                responseRemarks: remarksText,
                isActive: 1,
                approvalLevel: currentUserLevel
            };

            const approvalRes = await fetch(`${API_BASE_URL}api/T_PR_Approval`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(approvalData)
            });
            if (!approvalRes.ok) throw new Error('Failed to submit approval');

            await fetchPRApprovals();
            await fetchSecondApprovals(selectedPR.id);

            const allApprovalsRes = await fetch(`${API_BASE_URL}api/T_PR_Approval`).then(r => r.json());
            const prApprovalsList = allApprovalsRes.filter(a => a.prHeaderId === selectedPR.id && a.response === 1 && a.isActive === 1);
            const totalLevels = approverPositions.filter(p => p.pr === 1).length || 3;
            const isFullyApproved = prApprovalsList.length >= totalLevels;
            const newStatus = isFullyApproved ? 2 : 2; // ✅ Fully approved = status 2

            const fullPRData = await fetch(`${API_BASE_URL}api/PR_Header/${selectedPR.id}`).then(r => r.json());
            await fetch(`${API_BASE_URL}api/PR_Header/${selectedPR.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...fullPRData, status: newStatus })
            });

            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: isFullyApproved ? "FULLY-APPROVED" : "APPROVE",
                    module: "Purchase Request",
                    description: `${isFullyApproved ? "Fully approved" : `Approved Level ${currentUserLevel} of`} PR #${selectedPR.id}. Remarks: ${remarksText}`,
                    userId: sessionData.id || sessionData.userId,
                    username: sessionData.username,
                    fullName: `${sessionData.firstName} ${sessionData.lastName}`,
                })
            }).catch(err => console.error("Log failed:", err));

            if (onApprove) onApprove(selectedPR);
            try {
                const feedContent = isFullyApproved
                    ? `✅ PR has been FULLY APPROVED! ${remarksText !== "Approved via system" ? `\n📝 Remarks: ${remarksText}` : ""}`
                    : `✅ Level ${currentUserLevel} (${currentPositionName}) approved this PR. ${remarksText !== "Approved via system" ? `\n📝 Remarks: ${remarksText}` : ""}`;

                await fetch(`${API_BASE_URL}api/ActivityFeed`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        prHeaderId: selectedPR.id,
                        content: feedContent,
                        feedType: "text",
                        createdBy: currentUserId,
                        createdByName: sessionData.fullName || sessionData.username,
                    }),
                });
            } catch (err) {
                console.error("ActivityFeed post failed:", err);
            }
            if (autoApprovedLabels.length > 0) {
                await Swal.fire({
                    icon: "success",
                    title: "Approval Successful!",
                    html: `
                        <div style="text-align:left">
                            <p style="margin-bottom:8px"><strong>Auto-approved:</strong></p>
                            ${autoApprovedLabels.map(l => `<p>✅ ${l}</p>`).join('')}
                            <p style="margin-top:8px">✅ <strong>Level ${currentUserLevel} (${currentPositionName})</strong> — Approved by you</p>
                            ${isFullyApproved ? '<p style="margin-top:12px;color:green;font-weight:bold">🎉 PR is now FULLY APPROVED!</p>' : ''}
                        </div>
                    `,
                    timer: 6000
                });
            } else if (isFullyApproved) {
                await Swal.fire({ icon: "success", title: "PR Fully Approved", text: "All approval levels completed!" });
            } else {
                await Swal.fire({ icon: "success", title: `Level ${currentUserLevel} Approved`, text: "Approval recorded successfully." });
            }

        } catch (error) {
            console.error('❌ Error approving PR:', error);
            await Swal.fire({ icon: "error", title: "Approval Error", text: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ─── REJECT ───────────────────────────────────────────────────────────────
    const handleReject = async () => {
        const userSession = localStorage.getItem("user");
        if (!userSession) {
            await Swal.fire({ icon: "error", title: "No Session", text: "No user session found." });
            return;
        }

        const sessionData = JSON.parse(userSession);
        const currentUserId = sessionData.id || sessionData.userId;
        const userLevelId = sessionData.userLevelId || sessionData.userLevel;

        const { value: rejectionRemarks } = await Swal.fire({
            title: 'Reject Purchase Request?',
            input: 'textarea',
            inputLabel: 'Reason for Rejection',
            inputPlaceholder: 'Enter reason...',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Yes, Reject',
            inputValidator: (value) => { if (!value) return 'Please provide a reason for rejection!'; }
        });
        if (!rejectionRemarks) return;

        setIsSubmitting(true);
        try {
            const userPosition = approverPositions.find(pos => pos.approverUserLevelId === userLevelId);
            const currentUserLevel = userPosition?.colum || 1;
            const currentPositionName = userPosition?.approverPosition || '';

            // ✅ Always auto-reject ENDORSEMENT + ACKNOWLEDGEMENT regardless of level
            const autoRejectedLabels = await autoApproveLowerLevels(sessionData, currentUserLevel, false);

            const rejectionData = {
                id: 0, prHeaderId: selectedPR.id, approverUserLevelId: userLevelId,
                actualApproverId: currentUserId, response: 0,
                responseDate: new Date().toISOString(), responseRemarks: rejectionRemarks,
                isActive: 1, approvalLevel: currentUserLevel
            };

            const approvalRes = await fetch(`${API_BASE_URL}api/T_PR_Approval`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rejectionData)
            });
            if (!approvalRes.ok) throw new Error('Failed to submit rejection');

            await fetchPRApprovals();
            await fetchSecondApprovals(selectedPR.id);

            const fullPRData = await fetch(`${API_BASE_URL}api/PR_Header/${selectedPR.id}`).then(r => r.json());
            await fetch(`${API_BASE_URL}api/PR_Header/${selectedPR.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...fullPRData, status: 4 }) // ✅ Rejected = status 4
            });

            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "REJECT", module: "Purchase Request",
                    description: `Rejected PR #${selectedPR.id} at Level ${currentUserLevel} (${currentPositionName}). Reason: ${rejectionRemarks}`,
                    userId: sessionData.id || sessionData.userId,
                    username: sessionData.username,
                    fullName: `${sessionData.firstName} ${sessionData.lastName}`,
                })
            }).catch(err => console.error("Log failed:", err));

            if (onReject) onReject(selectedPR);

            // ✅ POST SA ACTIVITY FEED
            try {
                const feedContent = `❌ PR has been REJECTED at Level ${currentUserLevel} (${currentPositionName}).\n📝 Reason: ${rejectionRemarks}`;

                await fetch(`${API_BASE_URL}api/ActivityFeed`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        prHeaderId: selectedPR.id,
                        content: feedContent,
                        feedType: "text",
                        createdBy: currentUserId,
                        createdByName: sessionData.fullName || sessionData.username,
                    }),
                });
            } catch (err) {
                console.error("ActivityFeed post failed:", err);
            }

            if (autoRejectedLabels.length > 0) {
                await Swal.fire({
                    icon: 'error', title: 'PR Rejected!',
                    html: `
                        <div style="text-align:left">
                            <p style="margin-bottom:8px"><strong>Auto-rejected:</strong></p>
                            ${autoRejectedLabels.map(l => `<p>❌ ${l}</p>`).join('')}
                            <p style="margin-top:8px">❌ <strong>Level ${currentUserLevel} (${currentPositionName})</strong> — Rejected by you</p>
                            <p style="margin-top:12px;color:red;font-weight:bold">PR is now fully REJECTED.</p>
                        </div>
                    `,
                    confirmButtonColor: '#dc2626'
                });
            } else {
                await Swal.fire({ icon: 'error', title: 'Rejected!', text: 'PR rejected successfully!', confirmButtonColor: '#dc2626' });
            }

        } catch (error) {
            console.error('Error submitting rejection:', error);
            await Swal.fire({ icon: 'error', title: 'Rejection Failed', text: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ─── Approval workflow helpers ────────────────────────────────────────────
    const groupedApprovers = approverPositions.reduce((acc, approver) => {
        const level = approver.colum;
        if (!acc[level]) acc[level] = [];
        acc[level].push(approver);
        return acc;
    }, {});

    const getApprovalStatus = (level) => {
        const levelRejection = prApprovals.find(a => a.approvalLevel === level && a.response === 0 && a.isActive === 1);
        if (levelRejection) return 'rejected';
        const levelApproval = prApprovals.find(a => a.approvalLevel === level && a.response === 1 && a.isActive === 1);
        if (levelApproval) return 'approved';
        return 'waiting';
    };

    const totalLevels = approverPositions.filter(p => p.pr === 1).length || 3;
    const levelNumbers = Array.from({ length: totalLevels }, (_, i) => i + 1);

    // ─── Signature slots ──────────────────────────────────────────────────────
    const dynamicApproverSlots = approverPositions
        .filter(p => p.pr === 1)
        .sort((a, b) => a.colum - b.colum)
        .map(p => ({ key: `level_${p.colum}`, label: p.approverPosition.toUpperCase(), level: p.colum, source: 'approver' }));

    const signatureSlots = [
        { key: 'requestor', label: 'REQUESTOR', level: null, source: 'requestor' },
        { key: 'endorsement', label: 'ENDORSEMENT', level: 2, source: 'second' },
        { key: 'acknowledgement', label: 'ACKNOWLEDGEMENT', level: 3, source: 'second' },
        ...dynamicApproverSlots
    ];

    const getSignatureData = (slot) => {
        if (slot.source === 'requestor') {
            const requestorName = selectedPR?.requestor || `User ${selectedPR?.createdBy || "-"}`;
            const requestorData = requestors.find(r => r.name === requestorName);
            const signatureUrl = requestorData?.signature ? `${API_BASE_URL}api/Requestor/${requestorData.id}/signature` : null;
            return { name: requestorName, signatureUrl, date: selectedPR?.creationDate, useImgTag: true };
        } else if (slot.source === 'second') {
            // ✅ Find by approvalLevel (2 = Endorsement, 3 = Acknowledgement)
            const approval = secondApprovals.find(a => a.approvalLevel === slot.level);
            const signatureUrl = approval?.actualApproverId
                ? (approverSignatures[approval.actualApproverId] || null)
                : null;
            const userDetails = approval?.actualApproverId ? approverDetails[approval.actualApproverId] : null;
            const name = approval?.actualApproverName ||
                (userDetails ? `${userDetails.firstName} ${userDetails.lastName}`.trim() : "—");
            return { name, signatureUrl, date: approval?.responseDate, useImgTag: false };
        } else {
            const approval = prApprovals.find(a => a.approvalLevel === slot.level && a.response === 1 && a.isActive === 1);
            const signatureUrl = approval?.actualApproverId
                ? approverSignatures[approval.actualApproverId] || null
                : null;
            const userDetails = approval?.actualApproverId ? approverDetails[approval.actualApproverId] : null;
            const name = approval?.actualApproverName ||
                (userDetails ? `${userDetails.firstName} ${userDetails.lastName}`.trim() : "—");
            return { name, signatureUrl, date: approval?.responseDate, useImgTag: false };
        }
    };

    // ─── RENDER ───────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[98vh] overflow-y-auto">

                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white px-8 py-5 flex justify-between items-start sticky top-0 z-10">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                            <FileText className="w-7 h-7" />
                            <div>
                                <h3 className="text-xl font-bold tracking-wide">PURCHASE REQUISITION</h3>
                                <p className="text-blue-200 text-xs">Official Document</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 mt-3">
                            <div>
                                <p className="text-xs text-blue-300 uppercase tracking-wider">PR Number</p>
                                <p className="text-base font-bold">{selectedPR.prNo}</p>
                            </div>
                            <div className="h-8 w-px bg-blue-400" />
                            <div>
                                <p className="text-xs text-blue-300 uppercase tracking-wider">Status</p>
                                <div className="mt-1">{getStatusBadge(selectedPR.status)}</div>
                            </div>
                            <div>
                                <p className="text-xs text-blue-300 uppercase tracking-wider">Date Printed</p>
                                <p className="text-sm font-medium">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition-all ml-4">
                        <XCircle className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-8">

                    {/* ── PR Info ─────────────────────────────────────────── */}
                    <div className="border border-gray-300 rounded-md mb-6">
                        <div className="bg-gray-50 border-b border-gray-300 px-5 py-3 flex justify-between items-center">
                            <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide">PR Information</h4>
                            {selectedPR.controlNumber && (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Control Number:</span>
                                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full border border-blue-300">
                                        {selectedPR.controlNumber}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="px-5 py-4">
                            <div className="grid grid-cols-3 gap-x-8 gap-y-3 text-sm">
                                <div className="flex gap-1">
                                    <span className="font-semibold text-gray-700 w-32 flex-shrink-0">REQUESTED BY:</span>
                                    <span className="text-gray-600">{selectedPR.requestor || "—"}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="font-semibold text-gray-700 w-32 flex-shrink-0">Department / Sec:</span>
                                    <span className="text-gray-600">{selectedPR.department || "—"}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="font-semibold text-gray-700 w-32 flex-shrink-0">Date Needed:</span>
                                    <span className="text-gray-600">{formatDate(selectedPR.dateNeeded)}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="font-semibold text-gray-700 w-32 flex-shrink-0">Date Submitted:</span>
                                    <span className="text-gray-600">{formatDate(selectedPR.dateSubmittedForApproval)}</span>
                                </div>
                                <div className="flex gap-1 col-span-3">
                                    <span className="font-semibold text-gray-700 w-32 flex-shrink-0">Priority:</span>
                                    <span className={`font-semibold ${selectedPR.priority === 'Emergency Order' ? 'text-red-600' : 'text-gray-600'}`}>
                                        {selectedPR.priority || "—"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Remarks ─────────────────────────────────────────── */}
                    {selectedPR.remarks && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-4 mb-6">
                            <p className="text-xs text-amber-700 font-bold uppercase mb-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> Remarks
                            </p>
                            <p className="text-sm text-amber-900">{selectedPR.remarks}</p>
                        </div>
                    )}

                    {/* ── Items Table ─────────────────────────────────────── */}
                    <div className="border border-gray-300 rounded-md overflow-hidden mb-6">
                        <div className="bg-blue-600 text-white px-5 py-3 flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            <h4 className="font-bold text-sm uppercase">
                                Items Requested {prDetails.length > 0 && `(${prDetails.length})`}
                            </h4>
                        </div>
                        {loadingDetails ? (
                            <div className="text-center py-10">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-500 border-t-transparent" />
                                <p className="text-gray-500 mt-2 text-sm">Loading items...</p>
                            </div>
                        ) : prDetails.length === 0 ? (
                            <div className="text-center py-10 bg-gray-50">
                                <p className="text-gray-400 text-sm">No items found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-blue-600 text-white">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold">#</th>
                                            <th className="px-4 py-3 text-left font-semibold">Item Description</th>
                                            <th className="px-4 py-3 text-left font-semibold">Specification</th>
                                            <th className="px-4 py-3 text-center font-semibold">Qty</th>
                                            <th className="px-4 py-3 text-center font-semibold">UOM</th>
                                            <th className="px-4 py-3 text-left font-semibold">Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {prDetails.map((item, index) => (
                                            <tr key={item.id || index} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-4 py-3 font-semibold text-gray-600">{index + 1}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-900">{item.itemDescription}</td>
                                                <td className="px-4 py-3 text-gray-600">{item.materialFinish || "—"}</td>
                                                <td className="px-4 py-3 text-center font-semibold text-gray-800">{item.qty}</td>
                                                <td className="px-4 py-3 text-center text-gray-600">{item.uom}</td>
                                                <td className="px-4 py-3 text-gray-600 text-xs">{item.Remarks || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── Signature Section ───────────────────────────────── */}
                    {(() => {
                        const row1Slots = signatureSlots.filter(s =>
                            ['requestor', 'endorsement', 'acknowledgement'].includes(s.key)
                        );
                        const row2Slots = signatureSlots.filter(s => s.source === 'approver');

                        const renderSignatureRow = (slots) => (
                            <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(3, 1fr)` }}>
                                {slots.map((slot) => {
                                    const { name, signatureUrl, date } = getSignatureData(slot);
                                    return (
                                        <div key={slot.key} className="flex flex-col items-center text-center">
                                            <div className="h-14 flex items-end justify-center mb-1 w-full">
                                                {signatureUrl ? (
                                                    <img
                                                        src={signatureUrl}
                                                        alt={`${name} signature`}
                                                        className="h-12 max-w-[140px] object-contain"
                                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                                    />
                                                ) : null}
                                            </div>
                                            <p className="text-sm font-semibold text-gray-800">{name}</p>
                                            <div className="w-full border-b border-gray-500 my-1" />
                                            <p className="text-xs text-gray-500 mb-1">
                                                {date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                                            </p>
                                            <p className="text-xs text-gray-400">Signature over Printed Name</p>
                                            <p className="text-sm font-bold text-gray-800 mt-1 uppercase">{slot.label}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        );

                        return (
                            <div className="border-t-2 border-gray-300 pt-6 mb-6">
                                {renderSignatureRow(row1Slots)}
                                {row2Slots.length > 0 && <div className="border-t border-gray-300 my-6" />}
                                {row2Slots.length > 0 && renderSignatureRow(row2Slots)}
                            </div>
                        );
                    })()}

                    {/* ── Approval Workflow ───────────────────────────────── */}
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="w-4 h-4 text-gray-600" />
                            <h4 className="text-sm font-bold text-gray-700 uppercase">Approval Workflow</h4>
                        </div>
                        {loading ? (
                            <div className="text-center py-6">
                                <div className="inline-block animate-spin rounded-full h-5 w-5 border-4 border-gray-400 border-t-transparent" />
                            </div>
                        ) : (
                            <div className="flex items-center justify-between gap-2">
                                {levelNumbers.map((level, index) => {
                                    const approvers = groupedApprovers[level] || [];
                                    const status = getApprovalStatus(level);
                                    const colorMap = {
                                        approved: { card: 'bg-green-50 border-green-300', badge: 'bg-green-500', text: 'text-green-700', pill: 'bg-green-100 text-green-800' },
                                        rejected: { card: 'bg-red-50 border-red-300', badge: 'bg-red-500', text: 'text-red-700', pill: 'bg-red-100 text-red-800' },
                                        waiting: { card: 'bg-gray-50 border-gray-200', badge: 'bg-gray-300', text: 'text-gray-500', pill: 'bg-gray-100 text-gray-600' },
                                    };
                                    const colors = colorMap[status] || colorMap.waiting;
                                    return (
                                        <React.Fragment key={level}>
                                            <div className={`flex-1 rounded-md border p-3 ${colors.card}`}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs text-white ${colors.badge}`}>
                                                        {status === 'approved' ? <Check className="w-3 h-3" /> :
                                                            status === 'rejected' ? <X className="w-3 h-3" /> : level}
                                                    </div>
                                                    <span className={`text-xs font-semibold ${colors.text}`}>
                                                        {status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Awaiting'}
                                                    </span>
                                                </div>
                                                <p className="text-xs font-bold text-gray-700 mb-2">Level {level}</p>
                                                {approvers.map(a => (
                                                    <div key={a.id} className={`text-xs text-center px-2 py-1 rounded ${colors.pill}`}>
                                                        {a.approverPosition}
                                                    </div>
                                                ))}
                                            </div>
                                            {index < levelNumbers.length - 1 && (
                                                <div className={`w-6 h-0.5 flex-shrink-0 ${status === 'approved' ? 'bg-green-400' : 'bg-gray-300'}`} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Approved By Cards ──────────────────────────────── */}
                    {prApprovals.filter(a => a.response === 1).length > 0 && (
                        <div className="border border-green-200 rounded-md p-5 mb-6 bg-green-50/30">
                            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-green-200">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <h4 className="text-sm font-bold text-gray-700 uppercase">Approved By</h4>
                            </div>
                            <div className="flex flex-wrap gap-4">
                                {prApprovals.filter(a => a.response === 1).sort((a, b) => a.approvalLevel - b.approvalLevel).map((approval) => {
                                    const approverPosition = approverPositions.find(p => p.approverUserLevelId === approval.approverUserLevelId)?.approverPosition || 'Approver';
                                    const userDetails = approverDetails[approval.actualApproverId];
                                    const fullName = userDetails ? `${userDetails.firstName} ${userDetails.lastName}`.trim() : `User ${approval.actualApproverId}`;
                                    return (
                                        <div key={approval.id} className="w-64 bg-white border-2 border-green-300 rounded-lg p-4 shadow-sm">
                                            <div className="text-center mb-3">
                                                <span className="text-xs bg-green-100 text-green-800 font-semibold px-2 py-0.5 rounded-full">{approverPosition}</span>
                                            </div>
                                            <p className="text-sm font-bold text-gray-800 text-center mb-1">{fullName}</p>
                                            <p className="text-xs text-gray-500 text-center mb-3">{formatDate(approval.responseDate)}</p>
                                            <div className="border border-dashed border-green-300 rounded h-16 flex items-center justify-center bg-gray-50">
                                                {approverSignatures[approval.actualApproverId] ? (
                                                    <img src={approverSignatures[approval.actualApproverId]} alt="Signature" className="max-h-full max-w-full object-contain" />
                                                ) : (
                                                    <p className="text-xs text-gray-400">Loading...</p>
                                                )}
                                            </div>
                                            {approval.responseRemarks && (
                                                <p className="text-xs text-gray-500 italic mt-2 line-clamp-2">"{approval.responseRemarks}"</p>
                                            )}
                                            <div className="flex items-center justify-center gap-1 bg-green-600 text-white rounded py-1 mt-3">
                                                <Check className="w-3 h-3" />
                                                <span className="text-xs font-bold">APPROVED</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Rejected By Cards ──────────────────────────────── */}
                    {prApprovals.filter(a => a.response === 0).length > 0 && (
                        <div className="border border-red-200 rounded-md p-5 mb-6 bg-red-50/30">
                            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-red-200">
                                <XCircle className="w-4 h-4 text-red-600" />
                                <h4 className="text-sm font-bold text-gray-700 uppercase">Rejected By</h4>
                            </div>
                            <div className="flex flex-wrap gap-4">
                                {prApprovals.filter(a => a.response === 0).sort((a, b) => a.approvalLevel - b.approvalLevel).map((approval) => {
                                    const approverPosition = approverPositions.find(p => p.approverUserLevelId === approval.approverUserLevelId)?.approverPosition || 'Approver';
                                    const userDetails = approverDetails[approval.actualApproverId];
                                    const fullName = userDetails ? `${userDetails.firstName} ${userDetails.lastName}`.trim() : `User ${approval.actualApproverId}`;
                                    return (
                                        <div key={approval.id} className="w-64 bg-white border-2 border-red-300 rounded-lg p-4 shadow-sm">
                                            <div className="text-center mb-3">
                                                <span className="text-xs bg-red-100 text-red-800 font-semibold px-2 py-0.5 rounded-full">{approverPosition}</span>
                                            </div>
                                            <p className="text-sm font-bold text-gray-800 text-center mb-1">{fullName}</p>
                                            <p className="text-xs text-gray-500 text-center mb-3">{formatDate(approval.responseDate)}</p>
                                            {approval.responseRemarks && (
                                                <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                                                    <p className="text-xs text-red-700 font-semibold mb-1">Reason:</p>
                                                    <p className="text-xs text-gray-700 italic">"{approval.responseRemarks}"</p>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-center gap-1 bg-red-600 text-white rounded py-1">
                                                <X className="w-3 h-3" />
                                                <span className="text-xs font-bold">REJECTED</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Permission Warning ─────────────────────────────── */}
                    {!canApprovePR && !isCheckingPermissions && !allLevelsApproved() && (
                        <div className="bg-red-50 border border-red-300 rounded-md p-4 mb-5 flex items-center gap-3">
                            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div>
                                <p className="font-bold text-red-800 text-sm">Cannot Approve</p>
                                <p className="text-red-700 text-xs">{denialReason || "You cannot approve this Purchase Request at this time."}</p>
                            </div>
                        </div>
                    )}

                    {/* ── Action Buttons ─────────────────────────────────── */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleApprove}
                            disabled={isButtonDisabled}
                            className={`flex-1 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all ${isButtonDisabled
                                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'}`}
                        >
                            <CheckCircle className="w-5 h-5" />
                            {isCheckingPermissions ? 'Checking...' : isSubmitting ? 'Processing...' : allLevelsApproved() ? 'Fully Approved' : 'Approve Request'}
                        </button>
                        <button
                            onClick={handleReject}
                            disabled={isButtonDisabled}
                            className={`flex-1 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all ${isButtonDisabled
                                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                : 'bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg'}`}
                        >
                            <XCircle className="w-5 h-5" />
                            {isCheckingPermissions ? 'Checking...' : isSubmitting ? 'Processing...' : 'Reject Request'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PRApprovalModal;